import { buildChain, type Heartbeat, type HeartbeatPoint } from "@arcade/protocol";

/**
 * Records signed heartbeat checkpoints during play (DESIGN.md §8.3). Call `tick(score)` on
 * your game loop; the recorder throttles to `cadenceMs` so you don't over-report. At submit,
 * the SDK calls `build()` to produce the HMAC-chained trail.
 */
export class HeartbeatRecorder {
  private points: HeartbeatPoint[] = [];
  private readonly t0 = Date.now();
  private lastBeatMs = Number.NEGATIVE_INFINITY;

  constructor(
    private readonly token: string,
    private readonly sessionId: string,
    private readonly cadenceMs = 3000,
  ) {}

  /** Record a checkpoint if the cadence has elapsed (always records the first). */
  tick(scoreSoFar: number): void {
    const elapsedMs = Date.now() - this.t0;
    if (this.points.length === 0 || elapsedMs - this.lastBeatMs >= this.cadenceMs) {
      this.points.push({ elapsedMs, scoreSoFar });
      this.lastBeatMs = elapsedMs;
    }
  }

  /** Build the signed chain, appending a final beat at the submitted score. */
  build(finalScore: number): Promise<Heartbeat[]> {
    const elapsedMs = Date.now() - this.t0;
    const last = this.points[this.points.length - 1];
    if (!last || last.scoreSoFar !== finalScore) {
      this.points.push({
        elapsedMs: Math.max(elapsedMs, last?.elapsedMs ?? 0),
        scoreSoFar: finalScore,
      });
    }
    return buildChain(this.token, this.sessionId, this.points);
  }
}
